import { useEffect, type RefObject } from 'react'

/**
 * Sets `data-focused` on `marked` while focus is in `frame`'s document, so it can draw a focus
 * ring (`data-focused:outline-2 …`, WCAG 2.4.7). In Chromium a frame on another origin or
 * sandboxed matches no :focus or :focus-within in this page, so the mark follows this window's
 * focus instead: the window blurs as focus enters the frame, which is then its active element.
 */
export function useFrameFocus(frame: RefObject<HTMLIFrameElement | null>, marked: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const show = () => marked.current?.toggleAttribute('data-focused', frame.current !== null && document.activeElement === frame.current)
    let check = 0
    const showSoon = () => {
      show()
      // Where the frame becomes the active element only after the window's blur.
      window.clearTimeout(check)
      check = window.setTimeout(show, 0)
    }
    window.addEventListener('blur', showSoon)
    window.addEventListener('focus', show)
    document.addEventListener('focusin', show)
    return () => {
      window.clearTimeout(check)
      window.removeEventListener('blur', showSoon)
      window.removeEventListener('focus', show)
      document.removeEventListener('focusin', show)
    }
  }, [frame, marked])
}
